import { ref, computed, onMounted, onUnmounted, reactive } from 'vue'
import { useWindowSize, useScroll, useThrottleFn } from '@vueuse/core'

export function useProximityTracking() {
  // Detect if mobile based on window width
  const { width: windowWidth, height: windowHeight } = useWindowSize()
  const isMobile = computed(() => windowWidth.value < 768)

  // Mouse position (desktop)
  const mouseX = ref(0)
  const mouseY = ref(0)

  // Viewport center Y (mobile)
  const { y: scrollY } = useScroll(window, { throttle: 50 })
  const viewportCenterY = computed(() => scrollY.value + windowHeight.value / 2)

  // Track all registered components and their distances
  const components = reactive(new Map())

  // Throttled mouse move handler
  const handleMouseMove = useThrottleFn((event) => {
    mouseX.value = event.clientX
    mouseY.value = event.clientY
  }, 50)

  // Set up event listeners
  onMounted(() => {
    window.addEventListener('mousemove', handleMouseMove)
  })

  onUnmounted(() => {
    window.removeEventListener('mousemove', handleMouseMove)
  })

  // Register a component
  function register(id) {
    components.set(id, { distance: Infinity })
  }

  // Unregister a component
  function unregister(id) {
    components.delete(id)
  }

  // Update a component's distance
  function updateDistance(id, distance) {
    const component = components.get(id)
    if (component) {
      component.distance = distance
    }
  }

  // Get the closest component's distance
  const closestDistance = computed(() => {
    let minDist = Infinity
    for (const [_, component] of components) {
      if (component.distance < minDist) {
        minDist = component.distance
      }
    }
    return minDist
  })

  return {
    isMobile,
    mouseX,
    mouseY,
    viewportCenterY,
    register,
    unregister,
    updateDistance,
    closestDistance
  }
}
